import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';

export interface AgenticAnalysisSummaryLoadingProps {
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * Loading state for Analysis Summary - matches Figma design.
 * Shows "In Progress" badge, instructional text, and an animated gradient progress bar
 * (blue → teal → green) that sweeps left to right.
 */
const AgenticAnalysisSummaryLoading: React.FC<AgenticAnalysisSummaryLoadingProps> = ({
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    return (
        <ConfigProvider theme={theme}>
            <style>{`
                @keyframes agentic-summary-loading-sweep {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(400%); }
                }
            `}</style>
            <div
                className="bg-white rounded-lg border border-[rgba(0,0,0,0.06)] shadow-sm p-6"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-analysis-summary-loading
            >
                {/* Header: Title + In Progress badge */}
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-black">
                        Analysis Summary
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full font-['Open_Sans'] text-sm leading-[18px] text-[#71717a] bg-[#f4f4f5]">
                        In Progress
                    </span>
                </div>

                {/* Instructional text */}
                <p className="font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.65)] mb-4">
                    Summary generation is in progress, please wait while I generate it for you!
                </p>

                {/* Animated gradient bar - blue→teal→green strip moves left to right */}
                <div className="w-full h-2 rounded-sm overflow-hidden bg-[#e5e7eb]">
                    <div
                        className="h-full w-1/3 rounded-sm"
                        style={{
                            background: 'linear-gradient(to right, #3b82f6, #06b6d4, #22c55e)',
                            animation: 'agentic-summary-loading-sweep 2s ease-in-out infinite',
                        }}
                        role="progressbar"
                        aria-valuenow={undefined}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Summary generation in progress"
                    />
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticAnalysisSummaryLoading;
